import React, { useState, useRef } from "react";
import styled from "styled-components";
import { useSwipeable } from "react-swipeable";
import { useBoundingRect } from "../util/use-width";
import { ReactComponent as ChevronLeft } from "../../svgs/chevron-left.svg";
import { ReactComponent as ChevronRight } from "../../svgs/chevron-right.svg";

import ContentUnit from "./content-unit";
import { normalizeMedia } from "./utils/normalize-media";

const Root = styled.div`
  position: relative;
  width: 100%;
  left: 0;
  height: ${({ height }) => height}px;
`;

const StyledContent = styled(ContentUnit).attrs(({ offset }) => ({
  style: { left: offset },
}))`
  height: ${({ height }) => height}px;
  width: ${({ width }) => width}px;
  position: absolute !important;
  object-fit: cover;
  transition: left 1s, opacity 0.3s;
  -webkit-tap-highlight-color: transparent;
  user-select: ${({ active }) => (active ? "unset" : "none")};
  cursor: ${({ active }) => (active ? "unset" : "pointer")};
  opacity: ${({ active }) => (active ? 1 : 0.6)};
`;

const Chevron = styled.svg`
  top: ${({ height }) => height / 2}px;
  ${({ right }) => `${right ? "right" : "left"}: 10px`};
  background-color: #fff4;
  z-index: 100;
  color: black;
  transform: translateY(-50%);
  display: block;
  position: absolute;
  cursor: pointer;
  padding: 22px;
  border-radius: 100%;
  user-select: none;
  height: 70px;
  width: 70px;
  ${({ theme }) => theme.media.mobile} {
    height: 60px;
    width: 60px;
  }
  &:focus {
    outline: none;
  }
`;

function modularIndex(array, index) {
  let modularized = index % array.length;
  if (modularized < 0) modularized += array.length;
  return modularized;
}

const Caroussel = ({ slides, spacing = 32, height, aspectRatio }) => {
  // TODOS LOS HOOKS DEBEN IR PRIMERO (reglas de React Hooks)
  const [centerIndex, setCenterIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const ref = useRef();
  const boundingRect = useBoundingRect(ref);
  const swipeable = useSwipeable({
    onSwipedRight: () => setCenterIndex(centerIndex - 1),
    onSwipedLeft: () => setCenterIndex(centerIndex + 1),
  });

  // Guard clauses: validar datos críticos (DESPUÉS de los hooks)
  if (!slides || !Array.isArray(slides) || slides.length === 0) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "[Caroussel] No slides provided or slides array is empty. Skipping render."
      );
    }
    return null;
  }

  // Normalizar parámetros usando el helper
  // Usar el primer slide con imagen para obtener dimensiones si están disponibles
  const firstImageSlide = slides.find(
    (slide) => slide.image && (slide.image.fluid || slide.image.dimensions)
  );

  const normalized = normalizeMedia({
    aspectRatio,
    height,
    spacing,
    image: firstImageSlide?.image || null,
  });

  const { innerWidth: innerImageWidth, outerWidth: outerImageWidth, safeHeight } = normalized;

  // Reporting y validaciones en desarrollo
  if (process.env.NODE_ENV === "development") {
    // Contar slides con imágenes sin dimensions
    const imageSlides = slides.filter(
      (slide) => slide.image && (slide.image.fluid || slide.image.url)
    );
    const slidesWithoutDimensions = imageSlides.filter(
      (slide) => !slide.image.dimensions || !slide.image.dimensions.width || !slide.image.dimensions.height
    );

    if (slidesWithoutDimensions.length > 0) {
      console.warn(
        `[Caroussel] ⚠️ ${slidesWithoutDimensions.length} de ${imageSlides.length} slides con imágenes no tienen dimensions de Prismic.`,
        "Esto puede afectar la precisión del cálculo de aspect ratio.",
        "Para solucionarlo, asegúrate de que las imágenes en Prismic tengan dimensiones procesadas."
      );
    }

    // Warnings cuando falten parámetros editoriales
    if (aspectRatio === null || aspectRatio === undefined) {
      console.warn(
        "[Caroussel] ⚠️ aspectRatio del slice es null/undefined.",
        "Usando fallback desde image.dimensions o valor por defecto (16/9).",
        "Recomendación: Configurar aspectRatio en Prismic para este slice."
      );
    }
    if (height === null || height === undefined) {
      console.warn(
        "[Caroussel] ⚠️ height del slice es null/undefined.",
        "Usando altura por defecto de 100px.",
        "Recomendación: Configurar height en Prismic para este slice."
      );
    }
    if (spacing === null || spacing === undefined) {
      console.warn(
        "[Caroussel] ⚠️ spacing del slice es null/undefined.",
        "Usando espaciado por defecto de 0px.",
        "Recomendación: Configurar spacing en Prismic para este slice."
      );
    }
  }
  
  // Guard clause: manejar SSR y casos donde boundingRect es null
  const containerWidth = boundingRect
    ? Math.max(100, boundingRect.right - boundingRect.left)
    : 1000;

  const globalOffset = containerWidth / 2 - innerImageWidth / 2;
  const sideImages = Math.ceil((containerWidth / outerImageWidth - 1) / 2) * 2;
  const totalImages = Math.max(1, sideImages * 2 + 1);

  return (
    <Root
      height={safeHeight}
      {...swipeable}
      ref={(element) => {
        ref.current = element;
        swipeable.ref(element);
      }}
    >
      <Chevron
        as={ChevronLeft}
        height={safeHeight}
        onClick={() => setCenterIndex(centerIndex - 1)}
      />
      <Chevron
        as={ChevronRight}
        height={safeHeight}
        right
        onClick={() => setCenterIndex(centerIndex + 1)}
      />
      {Array.from({ length: totalImages }).map((_, i) => {
        const position = i - sideImages;
        const imageIndex = centerIndex + position;
        const slide = slides[modularIndex(slides, imageIndex)];
        const imageOffset = globalOffset + position * outerImageWidth;
        return (
          <StyledContent
            key={`${imageIndex}+${sideImages}`}
            {...slide}
            active={centerIndex === imageIndex}
            playing={centerIndex === imageIndex && playing}
            height={safeHeight}
            width={innerImageWidth}
            offset={imageOffset}
            onClick={() => {
              setCenterIndex(imageIndex);
            }}
            onPlayingChange={(newPlaying) =>
              setPlaying(centerIndex === imageIndex && newPlaying)
            }
          />
        );
      })}
    </Root>
  );
};

Caroussel.propTypes = {};

export default Caroussel;
